/**
 * La carte de la tournée : huit comptoirs, et où on en est.
 *
 * Rien ne se décide ici. L'état de chaque comptoir vient de `tableau-campagne.ts`,
 * les règles de déblocage de `shared/campagne.ts`, et la persistance du coffre. Ce
 * fichier n'est que du rendu — c'est ce qui permet de tester la campagne sans monter
 * un navigateur.
 *
 * ## Un comptoir gagné reste ouvert
 *
 * C'est le geste qui rend les défis jouables : on revient au Balto tenter le sec
 * qu'on n'a pas eu. Rejouer ne fait rien reculer, puisque le déblocage se déduit des
 * victoires et non d'un curseur de progression.
 */

import type { Progression } from "@zinc421/shared";

import { useTextes } from "../i18n/contexte.js";
import type { Textes } from "../i18n/index.js";
import {
  fichesComptoirs, lignesStatistiques, resumeAvancement, type FicheComptoir,
} from "./tableau-campagne.js";

interface Props {
  readonly progression: Progression;
  readonly nom: string;
  readonly onNom: (nom: string) => void;
  readonly onJouer: (numero: number) => void;
  readonly onQuitter: () => void;
  /** Ce que la dernière partie de campagne a donné, à afficher une fois. */
  readonly derniere: string | null;
  /** Vrai si le coffre a refusé d'écrire : la progression ne survivra pas. */
  readonly stockageMuet: boolean;
  readonly enPreparation: boolean;
}

export function Campagne({
  progression, nom, onNom, onJouer, onQuitter, derniere, stockageMuet, enPreparation,
}: Props) {
  const textes = useTextes();
  const fiches = fichesComptoirs(progression, textes);
  const stats = lignesStatistiques(progression.stats, textes);

  return (
    <div className="campagne">
      <h1>{textes.campagneTitre}</h1>
      <p className="accroche">{resumeAvancement(progression, textes)}</p>

      {derniere && <p className="campagne-resultat" role="status">{derniere}</p>}
      {stockageMuet && (
        <p className="alerte" role="alert">
          {textes.stockageMuet}
        </p>
      )}

      <label className="campagne-nom">
        <span>{textes.tonNomAuComptoir}</span>
        <input
          className="nom"
          type="text"
          value={nom}
          maxLength={16}
          placeholder={textes.tonNomPlaceholder}
          onChange={(e) => onNom(e.target.value)}
        />
      </label>

      <ol className="comptoirs">
        {fiches.map((fiche) => (
          <FicheDeComptoir
            key={fiche.numero}
            fiche={fiche}
            textes={textes}
            enPreparation={enPreparation}
            onJouer={() => onJouer(fiche.numero)}
          />
        ))}
      </ol>

      {stats.length > 0 && (
        <section className="stats" aria-label={textes.tesStatistiques}>
          <h2>{textes.depuisLeDebut}</h2>
          <dl>
            {stats.map((ligne) => (
              <div key={ligne.libelle} className="stat">
                <dt>{ligne.libelle}</dt>
                <dd>{ligne.valeur}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <button className="bouton bouton--discret" onClick={onQuitter}>
        {textes.revenirAuComptoir}
      </button>
    </div>
  );
}

function FicheDeComptoir({
  fiche, textes, onJouer, enPreparation,
}: {
  readonly fiche: FicheComptoir;
  readonly textes: Textes;
  readonly onJouer: () => void;
  readonly enPreparation: boolean;
}) {
  const classe = `comptoir comptoir--${fiche.etat.toLowerCase()}`;
  return (
    <li className={classe}>
      <button
        type="button"
        className="comptoir-bouton"
        disabled={!fiche.jouable || enPreparation}
        onClick={onJouer}
      >
        <span className="comptoir-rang" aria-hidden="true">{fiche.numero}</span>
        <span className="comptoir-corps">
          <span className="comptoir-lieu">
            {fiche.lieu}
            {fiche.etoile && (
              <span className="comptoir-etoile" aria-label={textes.defiReussiAria}>★</span>
            )}
            {fiche.etat === "GAGNE" && (
              <span className="comptoir-fait">{textes.comptoirGagne}</span>
            )}
          </span>
          <span className="comptoir-accroche">
            {fiche.jouable ? fiche.accroche : textes.comptoirVerrouille}
          </span>
          {fiche.jouable && (
            <>
              <span className="comptoir-face">
                {textes.comptoirATable(fiche.joueurs, fiche.adversaires.join(", "))}
                {fiche.soiree && textes.comptoirSoiree}
              </span>
              <span className={`comptoir-defi${fiche.etoile ? " comptoir-defi--fait" : ""}`}>
                {textes.comptoirDefi(fiche.defi)}
              </span>
            </>
          )}
          {fiche.tentatives > 0 && !fiche.jouable && (
            <span className="comptoir-face">{textes.tentatives(fiche.tentatives)}</span>
          )}
        </span>
      </button>
    </li>
  );
}
